import { normalizeEndingBranch } from './endingBranch.js';
import { cueHasMovement, estimateMovementDurationMs } from './automaticRehearsal.js';
import { normalizeCharacterVoices } from './characterVoices.js';

const COLORS = ['#55d7ff', '#ffb454', '#9b8cff', '#72e0a6', '#ff7fa6', '#f6df6b'];

function text(value, fallback = '', limit = 2000) {
  return String(value ?? fallback).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').trim().slice(0, limit);
}

function slug(value, fallback) {
  return text(value, fallback, 80).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || fallback;
}

function uniqueId(requested, fallback, used) {
  const base = slug(requested, fallback);
  let id = base;
  let suffix = 2;
  while (used.has(id)) id = `${base}-${suffix++}`;
  used.add(id);
  return id;
}

function characterNameKey(value) {
  return text(value, '', 80).replace(/\s+/g, ' ').toUpperCase();
}

function characterDetails(character, index, id) {
  const name = text(character.name, `Actor ${index + 1}`, 60);
  return {
    id, name, role: text(character.role, 'Character', 100),
    color: /^#[0-9a-f]{6}$/i.test(character.color) ? character.color : COLORS[index % COLORS.length],
    initials: text(character.initials, name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2), 3).toUpperCase(),
    overview: text(character.overview, `Play ${name}.`, 800), publicInfo: text(character.publicInfo, 'No public background supplied.', 1200),
    secret: text(character.secret, 'No private background supplied.', 1200), objective: text(character.objective, 'Support the scene.', 1200),
    relationships: text(character.relationships, '', 1200) || undefined,
  };
}

function asSentence(value, limit = 240) {
  const clean = text(value, '', limit);
  if (!clean) return '';
  const sentence = `${clean[0].toUpperCase()}${clean.slice(1)}`;
  return /[.!?…]$/.test(sentence) ? sentence : `${sentence}.`;
}

function lowerFirst(value) {
  const clean = text(value, '', 1000);
  return clean ? `${clean[0].toLowerCase()}${clean.slice(1)}` : '';
}

function lowerArticle(value) {
  return String(value).replace(/^(The|A|An)\b/, (article) => article.toLowerCase());
}

function readableList(values) {
  if (values.length < 2) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function words(value, limit) {
  const parts = text(value, '', 1200).split(/\s+/).filter(Boolean);
  if (parts.length <= limit) return parts.join(' ');
  return `${parts.slice(0, limit).join(' ').replace(/[,:;.!?]+$/, '')}…`;
}

function naturalSetting(scene) {
  let setting = text(scene.setting || scene.location || scene.title, 'the rehearsal space', 180);
  const heading = setting.match(/^(?:INT\.?\/?EXT\.?|EXT\.?\/?INT\.?|INT\.?|EXT\.?)\s+(.+?)(?:\s+-\s+(.+))?$/i);
  if (heading) {
    setting = heading[1].toLowerCase().replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
    const time = text(heading[2], '', 40).toLowerCase();
    return `You're ${/^EXT/i.test(scene.setting || scene.location) ? 'outside at' : 'in'} ${setting}${time ? ` at ${time}` : ''}.`;
  }
  if (/^(?:in|on|at|outside|inside|behind|beneath|near|within)\b/i.test(setting)) return asSentence(`You're ${lowerFirst(setting)}`);
  const preposition = /\b(room|hall|hallway|gallery|kitchen|station|bridge|basement|shelter|caf[eé]|house|school|lab|office|theater|theatre|yard|ship)\b/i.test(setting) ? 'in' : 'at';
  return asSentence(`You're ${preposition} ${lowerArticle(setting)}`);
}

function useful(value, excluded = []) {
  const clean = text(value, '', 1200);
  return clean && !excluded.some((pattern) => pattern.test(clean)) ? clean : '';
}

function sceneCharacters(cues, sceneId, characters) {
  const ids = new Set();
  for (const cue of cues) {
    if (cue.sceneId !== sceneId) continue;
    if (cue.characterId) ids.add(cue.characterId);
    for (const id of cue.characterIds || []) ids.add(id);
  }
  return characters.filter((character) => ids.has(character.id));
}

function generatedBriefText({ scenario, scene, character, cues, script, firstCharacterScene }) {
  const role = useful(character.role, [/^character$/i]);
  const identity = firstCharacterScene ? asSentence(`You are ${character.name}${role ? `, ${words(role, 8)}` : ''}`, 160) : '';
  const publicFact = firstCharacterScene ? useful(character.publicInfo, [/^no public background supplied\.?$/i]) : '';
  const privateFact = firstCharacterScene ? useful(character.secret, [/^no private background supplied\.?$/i]) : '';
  const where = asSentence(words(naturalSetting(scene), 16));
  const sceneSituation = useful(scene.situation, [/stay present/i, /follow the text/i]);
  const scenarioSituation = firstCharacterScene ? useful(scenario.logline, [/^\d+ scene/i, /^a live rehearsal scenario/i]) : '';
  const firstCue = cues.find((cue) => cue.sceneId === scene.id);
  const publicOpening = firstCue?.visibility === 'public' && !['dialogue', 'transition'].includes(firstCue.type) ? (firstCue.directionText || firstCue.text) : '';
  const happening = sceneSituation
      ? asSentence(words(sceneSituation, 16))
      : scenarioSituation
      ? asSentence(words(scenarioSituation, 16))
      : publicOpening ? asSentence(`At the start of this scene, ${lowerFirst(words(publicOpening, 14))}`) : '';
  const priorEvent = useful(scene.priorEvent, [/none/i]);
  const sceneIndex = scenario.scenes.findIndex((item) => item.id === scene.id);
  const previouslyKnownPeople = new Set(scenario.scenes.slice(0, sceneIndex).flatMap((item) => {
    const cast = sceneCharacters(cues, item.id, scenario.characters);
    return cast.some((entry) => entry.id === character.id) ? cast.map((entry) => entry.id) : [];
  }));
  const others = sceneCharacters(cues, scene.id, scenario.characters).filter((item) => item.id !== character.id && !previouslyKnownPeople.has(item.id)).slice(0, 3);
  const suppliedRelationships = firstCharacterScene ? useful(character.relationships) : '';
  const people = suppliedRelationships
    ? asSentence(words(suppliedRelationships, 14))
    : others.length
      ? asSentence(`The other people here are ${readableList(others.map((item) => `${item.name}${useful(item.role, [/^character$/i]) ? `, ${words(item.role, 5)}` : ''}`))}`, 260)
      : '';
  const objective = firstCharacterScene ? useful(character.objective, [/^support the scene\.?$/i]) : '';
  const want = objective ? asSentence(`Right now, ${lowerFirst(words(objective, 14))}`) : '';
  const prior = priorEvent ? asSentence(words(priorEvent, 14)) : '';
  const publicContext = publicFact ? asSentence(words(publicFact, 12)) : '';
  const privateContext = privateFact ? asSentence(`Only you know ${lowerFirst(words(privateFact, 12))}`) : '';
  const candidates = [
    { key: 'identity', text: identity, priority: 100 },
    { key: 'public', text: publicContext, priority: 65 },
    { key: 'where', text: where, priority: 98 },
    { key: 'happening', text: happening, priority: 96 },
    { key: 'prior', text: prior, priority: 90 },
    { key: 'people', text: people, priority: 75 },
    { key: 'private', text: privateContext, priority: 80 },
    { key: 'want', text: want, priority: 92 },
  ].filter((item) => item.text);
  const selected = new Set();
  let count = 0;
  for (const candidate of [...candidates].sort((left, right) => right.priority - left.priority)) {
    const size = candidate.text.split(/\s+/).length;
    if (selected.size >= 2 && count + size > 65) continue;
    selected.add(candidate.key);
    count += size;
  }
  return candidates.filter((candidate) => selected.has(candidate.key)).map((candidate) => candidate.text).join(' ');
}

function normalizeActorBriefs(inputBriefs, scenario, remapCharacterId, script) {
  const supplied = new Map();
  for (const brief of Array.isArray(inputBriefs) ? inputBriefs.slice(0, 480) : []) {
    const characterId = remapCharacterId(brief.characterId, brief.characterName);
    const sceneId = scenario.scenes.some((scene) => scene.id === brief.sceneId) ? brief.sceneId : null;
    const value = text(brief.text, '', 1600);
    if (!characterId || !sceneId || (!value && brief.source !== 'director')) continue;
    supplied.set(`${sceneId}:${characterId}`, {
      id: slug(brief.id, `brief-${sceneId}-${characterId}`), sceneId, characterId, text: value,
      source: brief.source === 'generated' ? 'generated' : 'director',
    });
  }
  const briefingCharacters = scene => {
    const ids = new Set(sceneCharacters(scenario.cues, scene.id, scenario.characters).map(character => character.id));
    for (const character of scenario.characters) if (supplied.has(`${scene.id}:${character.id}`)) ids.add(character.id);
    return scenario.characters.filter(character => ids.has(character.id));
  };
  const firstSceneByCharacter = new Map();
  for (const scene of scenario.scenes) {
    for (const character of briefingCharacters(scene)) {
      if (!firstSceneByCharacter.has(character.id)) firstSceneByCharacter.set(character.id, scene.id);
    }
  }
  const briefs = [];
  for (const scene of scenario.scenes) {
    for (const character of briefingCharacters(scene)) {
      const key = `${scene.id}:${character.id}`;
      const existing = supplied.get(key);
      briefs.push(existing?.source === 'director' ? existing : {
        id: `brief-${scene.id}-${character.id}`, sceneId: scene.id, characterId: character.id, source: 'generated',
        text: generatedBriefText({ scenario, scene, character, cues: scenario.cues, script, firstCharacterScene: firstSceneByCharacter.get(character.id) === scene.id }),
      });
    }
  }
  return briefs.filter((brief) => brief.text || brief.source === 'director');
}

function normalizeCharacters(rawCharacters) {
  const usedIds = new Set();
  const sourceIdMap = new Map();
  const nameMap = new Map();
  const characters = [];
  for (const [index, raw] of (Array.isArray(rawCharacters) ? rawCharacters : []).slice(0, 16).entries()) {
    const oldId = slug(raw.id || raw.name, `actor-${index + 1}`);
    const nameKey = characterNameKey(raw.name || `Actor ${index + 1}`);
    const existing = nameMap.get(nameKey);
    if (existing) {
      sourceIdMap.set(oldId, existing.id);
      continue;
    }
    const id = uniqueId(oldId, `actor-${index + 1}`, usedIds);
    const character = characterDetails(raw, index, id);
    sourceIdMap.set(oldId, id);
    nameMap.set(nameKey, character);
    characters.push(character);
  }
  return { characters, sourceIdMap, nameMap };
}

function normalizeScriptEntries(rawScript, scenes, characterData) {
  const sceneIds = new Set(scenes.map((scene) => scene.id));
  return (Array.isArray(rawScript) ? rawScript : []).slice(0, 1000).map((entry) => {
    const nameMatch = characterData.nameMap.get(characterNameKey(entry.speaker));
    const sourceCharacterId = slug(entry.characterId || entry.speaker, '');
    const characterId = characterData.sourceIdMap.get(sourceCharacterId) || nameMatch?.id;
    const character = characterData.characters.find((item) => item.id === characterId);
    const type = entry.type === 'dialogue' ? 'dialogue' : entry.type === 'scene' ? 'scene' : entry.type === 'transition' ? 'transition' : 'direction';
    return {
      type, sceneId: sceneIds.has(entry.sceneId) ? entry.sceneId : scenes[0].id,
      characterId: character?.id, speaker: character?.name || (text(entry.speaker, '', 60) || undefined), text: text(entry.text, '', 4000),
    };
  }).filter((entry) => entry.text);
}

function cuesFromEntries(entries, characters) {
  const byId = new Map(characters.map((character) => [character.id, character]));
  const cues = [];
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    if (entry.type === 'scene') continue;
    const character = entry.characterId ? byId.get(entry.characterId) : null;
    if (entry.type === 'direction' && character) {
      const directions = [entry.text];
      let nextIndex = index + 1;
      while (entries[nextIndex]?.type === 'direction' && entries[nextIndex].characterId === character.id && entries[nextIndex].sceneId === entry.sceneId) {
        directions.push(entries[nextIndex].text);
        nextIndex += 1;
      }
      const dialogue = entries[nextIndex];
      if (dialogue?.type === 'dialogue' && dialogue.characterId === character.id && dialogue.sceneId === entry.sceneId) {
        const directionText = directions.join(' ');
        cues.push({
          sceneId: entry.sceneId, type: 'dialogue', visibility: 'character', characterId: character.id,
          label: `${character.name} · direction and line`, text: `${directionText} ${dialogue.text}`,
          directionText, dialogueText: dialogue.text, requireAck: true, cueVoice: true,
        });
        index = nextIndex;
        continue;
      }
      const directionText = directions.join(' ');
      const movement = cueHasMovement({ type: 'direction', directionText });
      cues.push({ sceneId: entry.sceneId, type: 'direction', visibility: 'character', characterId: character.id, label: `${character.name} · private direction`, text: directionText, directionText, requireAck: true, directorVoice: true, completionMode: movement ? 'timer' : 'actor-confirm', estimatedDurationMs: movement ? estimateMovementDurationMs(directionText) : undefined });
      index = nextIndex - 1;
      continue;
    }
    if (entry.type === 'dialogue' && character) {
      cues.push({ sceneId: entry.sceneId, type: 'dialogue', visibility: 'character', characterId: character.id, label: `${character.name} · line`, text: entry.text, dialogueText: entry.text, requireAck: true, cueVoice: true });
      continue;
    }
    cues.push({ sceneId: entry.sceneId, type: entry.type === 'transition' ? 'transition' : 'action', visibility: 'public', label: entry.type === 'transition' ? 'Transition' : 'Stage direction', text: entry.text, directionText: entry.text, directorVoice: true });
  }
  return cues.map((cue, index) => ({ id: `cue-${index + 1}`, ...cue }));
}

export function scenarioFromParsed(parsed, title = 'Imported rehearsal', id = `imported-${Date.now()}`) {
  const included = (parsed?.entries || []).filter((entry) => entry.type !== 'ignored');
  if (included.length > 1000) throw new Error('This rehearsal exceeds 1,000 script blocks. Split it into smaller scenes before saving; no partial script was saved.');
  if ((parsed?.characters || []).length > 16) throw new Error('This rehearsal exceeds 16 characters. Remove mistaken names or split the script before saving.');
  if ((parsed?.scenes || []).length > 30) throw new Error('This rehearsal exceeds 30 scenes. Split the script before saving.');
  if (included.some((entry) => String(entry.text || '').length > 4000)) throw new Error('A script block exceeds 4,000 characters. Split that block before saving; no text was discarded.');
  if (!included.some((entry) => !['scene', 'transition'].includes(entry.type) && String(entry.text || '').trim())) throw new Error('There is no dialogue or directions to rehearse. Add script text and analyze it again.');
  if (included.some((entry) => entry.type === 'dialogue' && !(parsed?.characters || []).some((character) => character.id === entry.characterId))) throw new Error('Assign every dialogue block to a character before saving.');
  const characterData = normalizeCharacters(parsed?.characters || []);
  const scenes = (parsed?.scenes || []).slice(0, 30).map((scene, index) => ({
    id: slug(scene.id, `scene-${index + 1}`), number: index + 1,
    title: text(scene.title || scene.heading, `Scene ${index + 1}`, 120),
    location: text(scene.location || scene.heading, 'Imported script', 160), setting: text(scene.setting || scene.location || scene.heading, 'Imported script', 200), tone: text(scene.tone, 'Follow the text and your scene partner.', 160),
  }));
  if (!scenes.length) scenes.push({ id: 'scene-1', number: 1, title: 'Scene 1', location: 'Imported script', tone: 'Follow the text and your scene partner.' });
  const rawSceneMap = new Map((parsed?.scenes || []).map((scene, index) => [scene.id, scenes[index]?.id]));
  const mappedEntries = (parsed?.entries || []).filter((entry) => entry.type !== 'ignored').map((entry) => ({ ...entry, sceneId: rawSceneMap.get(entry.sceneId) || entry.sceneId }));
  const entries = normalizeScriptEntries(mappedEntries, scenes, characterData);
  const cues = cuesFromEntries(entries, characterData.characters);
  if (cues.some((cue) => ['text', 'directionText', 'dialogueText'].some((field) => String(cue[field] || '').length > 4000))) throw new Error('Combined directions and dialogue exceed 4,000 characters in one cue. Split those blocks before saving; no text was discarded.');
  if (!cues.length) cues.push({ id: 'cue-1', sceneId: scenes[0].id, type: 'scene', visibility: 'public', label: 'Begin the scene', text: 'The director begins the imported rehearsal.', directionText: 'The director begins the imported rehearsal.', directorVoice: true });
  return normalizeScenario({
    schemaVersion: 3, id, title, category: 'Script rehearsal', mode: 'scripted', duration: 'Custom',
    logline: `${scenes.length} scene${scenes.length === 1 ? '' : 's'} · ${characterData.characters.length} character${characterData.characters.length === 1 ? '' : 's'}`,
    description: 'Imported locally. The original text is preserved on this device.', characters: characterData.characters, scenes, cues, script: entries,
    originalScript: text(parsed?.original, '', 200000), importedAt: new Date().toISOString(),
  });
}

export function validateScenarioForSave(input) {
  const sceneIds = new Set((input.scenes || []).map(scene => scene.id));
  const characterIds = new Set((input.characters || []).map(character => character.id));
  for (const [field, maximum, label] of [['characters', 16, 'characters'], ['scenes', 30, 'scenes'], ['cues', 1000, 'cues']]) {
    if ((input[field] || []).length > maximum) throw new Error(`This rehearsal exceeds ${maximum.toLocaleString('en-US')} ${label}. Split it before saving; no content was discarded.`);
  }
  for (const [index, cue] of (input.cues || []).entries()) {
    if (!sceneIds.has(cue.sceneId)) throw new Error(`Cue ${index + 1} refers to a removed scene. Reassign its scene or remove the cue before saving.`);
    const recipients = cue.visibility === 'character' ? [cue.characterId] : cue.visibility === 'selected' ? (cue.characterIds || []) : null;
    if (recipients && (!recipients.length || recipients.some(id => !characterIds.has(id)))) throw new Error(`Cue ${index + 1} needs an existing recipient. Reassign its recipient or remove the cue before saving.`);
    for (const field of ['text', 'directionText', 'spokenDirectionText', 'dialogueText']) {
      if (String(cue[field] || '').length > 4000) throw new Error(`Cue ${index + 1} exceeds 4,000 characters. Split its directions or dialogue before saving; no text was discarded.`);
    }
  }
}

export function normalizeScenario(input) {
  if (!input || typeof input !== 'object') throw new Error('Choose a valid rehearsal scenario.');
  const id = slug(input.id || input.title, 'scenario');
  const characterData = normalizeCharacters(input.characters);
  const characters = characterData.characters;
  const origins = (Array.isArray(input.characters) ? input.characters : []).slice(0, 16).map((c, i) => ({ id: slug(c.id || c.name, `actor-${i + 1}`), name: characterNameKey(c.name || `Actor ${i + 1}`) }));
  const ambiguousNames = new Set(origins.filter(c => origins.filter(o => o.id === c.id).length > 1 || origins.filter(o => o.name === c.name).length > 1).map(c => c.name));
  const ambiguousIds = new Set(characters.filter(c => ambiguousNames.has(characterNameKey(c.name))).map(c => c.id));
  const characterIds = new Set(characters.map((character) => character.id));
  const scenes = (Array.isArray(input.scenes) ? input.scenes : []).slice(0, 30).map((scene, index) => ({
    id: slug(scene.id, `scene-${index + 1}`), number: index + 1, title: text(scene.title || scene.heading, `Scene ${index + 1}`, 120),
    location: text(scene.location || scene.heading, 'Rehearsal space', 160), setting: text(scene.setting || scene.location || scene.heading, 'Rehearsal space', 200), tone: text(scene.tone, 'Stay present and listen.', 160),
    situation: text(scene.situation, '', 800) || undefined, priorEvent: text(scene.priorEvent, '', 800) || undefined,
  }));
  if (!scenes.length) scenes.push({ id: 'scene-1', number: 1, title: 'Scene 1', location: 'Rehearsal space', tone: 'Stay present and listen.' });
  const sceneIds = new Set(scenes.map((scene) => scene.id));
  const characterByName = new Map(characters.map((character) => [characterNameKey(character.name), character]));
  const remapCharacterId = (requested, speaker) => characterData.sourceIdMap.get(slug(requested || speaker, '')) || characterByName.get(characterNameKey(speaker))?.id;
  const cues = (Array.isArray(input.cues) ? input.cues : []).slice(0, 1000).map((cue, index) => {
    const visibility = ['public', 'character', 'selected'].includes(cue.visibility) ? cue.visibility : 'public';
    const normalized = {
      id: slug(cue.id, `cue-${index + 1}`), sceneId: sceneIds.has(cue.sceneId) ? cue.sceneId : scenes[0].id,
      type: text(cue.type, 'action', 40), visibility, label: text(cue.label, `Cue ${index + 1}`, 160), text: text(cue.text, '', 4000),
      directionCaptionOnly: cue.directionCaptionOnly === true || undefined,
      spokenDirectionPending: cue.spokenDirectionPending === true || undefined,
      requireAck: Boolean(cue.requireAck), directorVoice: Boolean(cue.directorVoice), cueVoice: Boolean(cue.cueVoice),
      directionText: text(cue.directionText, '', 4000) || undefined, spokenDirectionText: text(cue.spokenDirectionText, '', 4000) || undefined, dialogueText: text(cue.dialogueText, '', 4000) || undefined,
      completionMode: ['speech', 'timer', 'actor-confirm', 'director', 'movement'].includes(cue.completionMode) ? cue.completionMode : undefined,
      estimatedDurationMs: Number.isFinite(Number(cue.estimatedDurationMs)) ? Math.max(500, Math.min(30000, Math.round(Number(cue.estimatedDurationMs)))) : undefined,
      interruptAt: Number.isFinite(Number(cue.interruptAt)) ? Math.max(0, Math.min(1, Number(cue.interruptAt))) : undefined,
      situationUpdateText: text(cue.situationUpdateText, '', 800) || undefined,
    };
    const mappedId = remapCharacterId(cue.characterId, cue.speaker);
    if (visibility === 'character' && characterIds.has(mappedId)) normalized.characterId = mappedId;
    if (visibility === 'selected') normalized.characterIds = (Array.isArray(cue.characterIds) ? cue.characterIds : []).map((value) => remapCharacterId(value)).filter((value) => characterIds.has(value)).slice(0, 16);
    return normalized;
  }).filter((cue) => cue.text || cue.directionText || cue.dialogueText).map((cue) => ({ ...cue, text: cue.text || [cue.directionText, cue.dialogueText].filter(Boolean).join(' ') }));
  if (!cues.length) cues.push({ id: 'cue-1', sceneId: scenes[0].id, type: 'scene', visibility: 'public', label: 'Begin', text: 'Begin the rehearsal.', requireAck: false, directorVoice: true, cueVoice: false });
  const script = normalizeScriptEntries(input.script, scenes, characterData);
  const normalized = {
    schemaVersion: 3, id, title: text(input.title, 'Untitled rehearsal', 100), category: text(input.category, 'Rehearsal', 60), mode: text(input.mode, 'scripted', 40),
    duration: text(input.duration, 'Custom', 40), logline: text(input.logline, 'A live rehearsal scenario.', 300), description: text(input.description, '', 1200),
    characters, characterVoices: normalizeCharacterVoices(input.characterVoices, characters, ambiguousIds), scenes, cues, script, originalScript: text(input.originalScript, '', 200000), importedAt: text(input.importedAt, '', 80) || undefined,
  };
  normalized.endingBranch = normalizeEndingBranch(input.endingBranch, cues, characters);
  normalized.actorBriefs = normalizeActorBriefs(input.actorBriefs, normalized, remapCharacterId, script);
  return normalized;
}

export function renameScenarioCharacter(input, characterId, nextName) {
  const name = text(nextName, '', 60);
  if (!name) return structuredClone(input);
  const previous = input.characters.find((character) => character.id === characterId)?.name || '';
  return {
    ...structuredClone(input),
    characters: input.characters.map((character) => character.id === characterId ? { ...character, name, initials: name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() } : character),
    cues: input.cues.map((cue) => cue.characterId === characterId ? { ...cue, label: cue.label?.replace(previous, name) } : cue),
    script: (input.script || []).map((entry) => entry.characterId === characterId ? { ...entry, speaker: name } : entry),
    actorBriefs: (input.actorBriefs || []).map((brief) => brief.characterId === characterId ? { ...brief, text: brief.text?.replaceAll(previous, name) } : brief),
  };
}

export function editCueText(cue, field, value) {
  const next = { ...cue, [field]: value };
  if (field === 'text') {
    if (cue.dialogueText !== undefined && cue.directionText === undefined) next.dialogueText = value;
    if (cue.directionText !== undefined && cue.dialogueText === undefined) { next.directionText = value; delete next.spokenDirectionText; }
  } else if (field === 'directionText' || field === 'dialogueText') {
    if (field === 'directionText') delete next.spokenDirectionText;
    if (field === 'directionText' && cue.type === 'dialogue' && cue.dialogueText == null) next.dialogueText = cue.text;
    next.text = [next.directionText, next.dialogueText].filter(Boolean).join(' ');
    if (field === 'dialogueText') {
      next.type = value.trim() ? 'dialogue' : 'direction';
      next.cueVoice = Boolean(value.trim());
      next.directorVoice = !value.trim();
      if (!value.trim()) delete next.completionMode;
    }
  }
  return next;
}
